import { css, styled } from "styled-components";

import { PlayIconCircled, SectionTitleUnderlined, SourceLabel } from "common/components";
import { ListItemWrapper, type ListItemWrapperProps } from "common/components/lists/ListItemWrapper";
import { ListImage, ListLink } from "common/components/lists/ListLink";
import type { ListItemType } from "common/components/lists/types";
import { breakWordStyle, lineTruncationStyle } from "common/styles";
import type { Source } from "entities/knowledge/types";

const StyledTitle = styled(SectionTitleUnderlined)`
  & > h2 {
    font-weight: bold;
    font-size: 1.1rem;
    line-height: 1.5rem;
  }
  margin-top: 8px;
  margin-bottom: 16px;

  ${lineTruncationStyle(2)}
`;

const StyledSummary = styled.p`
  font-size: 0.9rem;
  line-height: 1.4rem;
  margin: 0;
  flex-grow: 1;
  color: ${({ theme }) => theme.colors.clusterCaptionText};

  ${breakWordStyle}
  ${lineTruncationStyle(3)};
`;

const StyledContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: start;
  margin: 0;
`;

const StyledDarkenLayer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.25);
`;

// TODO theming: icon is not resizable right now
const StyledPlayIcon = styled(PlayIconCircled)<{ type?: ListItemType }>`
  position: absolute;
  width: 44px;
  height: 44px;
  z-index: 1;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  ${({ type }) =>
    type === "large" &&
    css`
      width: 80px;
      height: 80px;
    `}
`;

const StyledImage = styled(ListImage)`
  border-radius: 10px;
  width: 120px;
  min-width: 120px;
  height: 90px;
`;

const StyledRow = styled.div`
  display: flex;
  height: 100%;
`;

type VideoListItemProps = ListItemWrapperProps & {
  title: string;
  description?: string;
  imageUrl?: string;
  source: Source;
  timestamp: number;
  isSelected?: boolean;
  link: string;
  onClick?: () => void;
};

export const VideoListItem = ({
  title,
  description,
  imageUrl,
  source,
  timestamp,
  link,
  onClick,
  ...listItemWrapperProps
}: VideoListItemProps) => (
  <ListItemWrapper {...listItemWrapperProps}>
    <StyledRow>
      <ListLink to={link} onClick={onClick}>
        {source && <SourceLabel source={source} date={timestamp} type="small" />}
        <StyledTitle>{title}</StyledTitle>
        <StyledContent>
          <div style={{ position: "relative", marginRight: "1rem" }}>
            <StyledImage src={imageUrl} />
            <StyledDarkenLayer />
            <StyledPlayIcon />
          </div>
          <StyledSummary>{description}</StyledSummary>
        </StyledContent>
      </ListLink>
    </StyledRow>
  </ListItemWrapper>
);
